import {
  CoreInputModelUtil,
  getNewErrors,
  InputErrorModel,
} from "@mindmemobile/mm-shared-global";
import {
  IAPIDonationItem,
  IAPIDonationItemIAPIResponse,
  IAPIDonationItems,
  IAPIFundraiserDonationModel,
  IDonation,
} from "../interfaces";
import { autorun } from "mobx";
import {
  ContactReferenceModel,
  CountryWithStateModel,
  MerchantAccount,
  PaymentMethodModel,
} from "@mindmemobile/mm-payment-modules";

export class DonationModel {
  id: string;
  amount: number;
  donorName: string;
  firstName?: string;
  lastName?: string;
  isAnonymous: boolean;
  donorEmail?: string;
  donorMobile?: string;
  comment: string;
  timestamp: Date;
  fundraiserId: string;
  promoterId?: string;
  isMonthly?: boolean;
  paymentMethod: PaymentMethodModel;
  merchantAccount: MerchantAccount;
  paymentTransactionId: string;

  constructor(data?: Partial<DonationModel>) {
    this.id = data?.id ?? "";
    this.amount = data?.amount ?? 0;
    this.donorName = data?.donorName ?? "";
    this.firstName = data?.firstName ?? "";
    this.lastName = data?.lastName ?? "";
    this.isAnonymous = data?.isAnonymous ?? false;
    this.donorEmail = data?.donorEmail ?? "";
    this.donorMobile = data?.donorMobile ?? "";
    this.comment = data?.comment ?? "";
    this.timestamp = data?.timestamp ? new Date(data.timestamp) : new Date();
    this.fundraiserId = data?.fundraiserId ?? "";
    this.promoterId = data?.promoterId ?? "";
    this.isMonthly = data?.isMonthly ?? false;
    this.paymentMethod = data?.paymentMethod
      ? new PaymentMethodModel(data.paymentMethod)
      : new PaymentMethodModel();
    this.merchantAccount = data?.merchantAccount
      ? new MerchantAccount(data.merchantAccount)
      : new MerchantAccount();
    this.paymentTransactionId = data?.paymentTransactionId || "";
  }

  getDisplayName() {
    // In admin views, always show actual name regardless of isAnonymous flag
    if (this.firstName && this.lastName) {
      return `${this.firstName} ${this.lastName}`;
    }
    // Fallback to donorName, but exclude generic anonymous names
    if (this.isAnonymous) {
      return "Anonymous Donor";
    }
    return "Unknown Donor";
  }

  static deserializeAPIModel(data: IAPIFundraiserDonationModel): DonationModel {
    const contact = ContactReferenceModel.deserializeAPIModel(data?.contact);
    return new DonationModel({
      id: data?.paymentTransactionId,
      fundraiserId: data?.fundRaiserId,
      amount: data?.amount,
      donorName: contact.displayValue,
      firstName: contact.firstName,
      lastName: contact.lastName,
      isAnonymous: false,
      donorEmail: contact.email,
      paymentTransactionId: data?.paymentTransactionId,
      donorMobile:
        contact.mobilePhone?.phoneNumber ||
        contact.homePhone?.phoneNumber ||
        contact.workPhone?.phoneNumber,
      comment: "",
      timestamp: data?.createdDate ? new Date(data.createdDate) : new Date(),
      paymentMethod:
        data?.paymentMethod &&
        PaymentMethodModel.deserializeAPIModel(data?.paymentMethod),
      merchantAccount:
        data?.paymentMethod?.merchantAccount &&
        MerchantAccount.deserializeAPIModel2(
          data?.paymentMethod?.merchantAccount
        ),
    });
  }
}

export class DonationItemModel {
  id: string;
  name: string;
  description: string;
  isActtive?: boolean;
  constructor(data?: Partial<DonationItemModel>) {
    this.id = data?.id ?? "";
    this.name = data?.name ?? "";
    this.description = data?.description ?? "";
  }

  static deserialize(data: IAPIDonationItemIAPIResponse): DonationItemModel {
    return new DonationItemModel({
      id: data?.id,
      name: data?.name,
    });
  }

  static deserializeAPIModel(data: IAPIDonationItem): DonationItemModel {
    return new DonationItemModel({
      id: data?.donationId,
      name: data?.donationName,
      description: data?.description,
    });
  }

  static deserializeAPIModel2(data: IAPIDonationItems): DonationItemModel {
    return new DonationItemModel({
      id: data?.id,
      name: data?.name,
      isActtive: data?.isActive == true,
    });
  }
}

export class DonorInfo {
  firstName: string = "";
  lastName: string = "";
  streetAddress: string = "";
  zip: string = "";
  city: string = "";
  state: string = "";
  country: string = "";
  email: string = "";
  phone: string = "";
  phoneCountryId: string = "";
  phoneCountryCode: string = "";
  isAnonymous: boolean = false;
  receiveNotifications: boolean = true;
  countryDetails: CountryWithStateModel;
  errors: InputErrorModel<DonorInfo>[] = [];
  constructor(init?: Partial<DonorInfo>) {
    this.city = init?.city || "";
    this.state = init?.state || "";
    this.zip = init?.zip || "";
    this.firstName = init?.firstName || "";
    this.lastName = init?.lastName || "";
    this.streetAddress = init?.streetAddress || "";
    this.country = init?.country || "";
    this.email = init?.email || "";
    this.phone = init?.phone || "";
    this.phoneCountryId = init?.phoneCountryId || "";
    this.phoneCountryCode = init?.phoneCountryCode || "";
    this.isAnonymous = init?.isAnonymous || false;
    this.receiveNotifications = init?.receiveNotifications ?? true;
    this.errors =
      (init &&
        init.errors?.map((e) => {
          return new InputErrorModel<DonorInfo>(e);
        })) ||
      [];
    this.countryDetails = init?.countryDetails || new CountryWithStateModel();
    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof DonorInfo): boolean {
    return this.errors.some((e) => e.field === field);
  }
  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<DonorInfo>[] = [];
    CoreInputModelUtil.validateTextField(
      this.firstName,
      "firstName",
      newErrors
    );
    CoreInputModelUtil.validateTextField(this.firstName, "lastName", newErrors);
    if (this.phone) {
      CoreInputModelUtil.validatePhoneNumber("phone", this.phone, newErrors);
      CoreInputModelUtil.validateTextField(
        this.phoneCountryId,
        "phoneCountryId",
        newErrors
      );
    }
    CoreInputModelUtil.validateTextField(this.country, "country", newErrors);
    CoreInputModelUtil.validateTextField(this.state, "state", newErrors);
    CoreInputModelUtil.validateTextField(this.city, "city", newErrors);
    CoreInputModelUtil.validateTextField(
      this.streetAddress,
      "streetAddress",
      newErrors
    );
    CoreInputModelUtil.validateTextField(this.zip, "zip", newErrors);
    CoreInputModelUtil.validateEmail("email", this.email, newErrors);

    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }
}
